<template>
  <div v-if="isOpen" class="publish-settings-overlay" @mousedown="handleOverlayMouseDown" @mouseup="handleOverlayMouseUp">
    <div class="publish-settings-content" @mousedown="handleContentMouseDown">
      <h3 class="publish-settings-title">ブクマすを公開する</h3>

      <div class="publish-settings-body">
        <p class="publish-description">
          ブックマークを<strong>読み取り専用の公開URL</strong>として共有できます。<br>
          公開されたブクマすはPINコード不要で誰でもアクセスできます。
        </p>

        <div class="sns-input-section">
          <label class="sns-label">SNSアカウント（任意）</label>
          <p class="sns-hint">
            製作者情報として表示されます。未入力の場合は「製作者：不明」と表示されます。
          </p>
          <input
            v-model="snsUrl"
            type="url"
            class="sns-input"
            placeholder="https://twitter.com/username"
            @input="detectPlatform"
          >
          <div v-if="detectedPlatform" class="platform-preview">
            <v-icon size="20" class="platform-icon">{{ platformIcon }}</v-icon>
            <span class="platform-name">{{ platformLabel }}</span>
            <span v-if="detectedUsername" class="platform-username">@{{ detectedUsername }}</span>
          </div>
        </div>

        <div class="comment-input-section">
          <label class="sns-label">コメント（任意）</label>
          <p class="sns-hint">
            OGP画像に表示されるコメントです。SNSでシェアした際に表示されます。
          </p>
          <textarea
            v-model="comment"
            class="comment-input"
            placeholder="例: お気に入りのサイトをまとめました！"
            maxlength="200"
            rows="3"
          ></textarea>
          <div class="comment-counter">{{ comment.length }} / 200</div>
        </div>

        <!-- OGPプレビュー -->
        <div class="ogp-preview-section">
          <label class="sns-label">OGPプレビュー</label>
          <p class="sns-hint">SNSでシェアされた際に表示される画像です。</p>
          <div class="ogp-preview-container">
            <img
              v-if="ogpPreviewUrl"
              :src="ogpPreviewUrl"
              class="ogp-preview-image"
              alt="OGPプレビュー"
            >
            <div v-else class="ogp-preview-loading">
              <v-icon size="24" color="#aaa">mdi-image-outline</v-icon>
              <span>プレビュー生成中...</span>
            </div>
          </div>
        </div>
      </div>

      <div class="publish-settings-footer">
        <button class="publish-cancel-button" @click="close">キャンセル</button>
        <button class="publish-submit-button" @click="publish" :disabled="isPublishing">
          {{ isPublishing ? '公開中...' : '公開する' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { getCookie, setCookie } from '../services/cookieService';
import { generateOgpImage } from '../services/ogpGenerator';

export default {
  name: 'PublishSettingsModal',
  props: {
    isOpen: {
      type: Boolean,
      required: true
    }
  },
  emits: ['close', 'publish'],
  data() {
    return {
      snsUrl: '',
      comment: '',
      detectedPlatform: '',
      detectedUsername: '',
      isPublishing: false,
      mouseDownOnOverlay: false,
      ogpPreviewUrl: '',
      previewTimer: null
    };
  },
  computed: {
    platformIcon() {
      const icons = {
        twitter: 'mdi-twitter',
        instagram: 'mdi-instagram',
        facebook: 'mdi-facebook',
        youtube: 'mdi-youtube',
        tiktok: 'mdi-music-note',
        github: 'mdi-github',
        other: 'mdi-account'
      };
      return icons[this.detectedPlatform] || '';
    },
    platformLabel() {
      const labels = {
        twitter: 'Twitter / X',
        instagram: 'Instagram',
        facebook: 'Facebook',
        youtube: 'YouTube',
        tiktok: 'TikTok',
        github: 'GitHub',
        other: 'その他'
      };
      return labels[this.detectedPlatform] || '';
    }
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.snsUrl = getCookie('bkms_publish_sns_url') || '';
        this.comment = getCookie('bkms_publish_comment') || '';
        this.detectedPlatform = '';
        this.detectedUsername = '';
        this.isPublishing = false;
        this.ogpPreviewUrl = '';
        if (this.snsUrl) {
          this.detectPlatform();
        }
        this.updateOgpPreview();
      } else {
        if (this.previewTimer) {
          clearTimeout(this.previewTimer);
          this.previewTimer = null;
        }
      }
    },
    comment() {
      this.schedulePreviewUpdate();
    }
  },
  methods: {
    detectPlatform() {
      const url = this.snsUrl.toLowerCase().trim();
      if (!url) {
        this.detectedPlatform = '';
        this.detectedUsername = '';
        this.schedulePreviewUpdate();
        return;
      }

      if (url.includes('twitter.com') || url.includes('x.com')) {
        this.detectedPlatform = 'twitter';
      } else if (url.includes('instagram.com')) {
        this.detectedPlatform = 'instagram';
      } else if (url.includes('facebook.com')) {
        this.detectedPlatform = 'facebook';
      } else if (url.includes('youtube.com')) {
        this.detectedPlatform = 'youtube';
      } else if (url.includes('tiktok.com')) {
        this.detectedPlatform = 'tiktok';
      } else if (url.includes('github.com')) {
        this.detectedPlatform = 'github';
      } else if (url.startsWith('http')) {
        this.detectedPlatform = 'other';
      } else {
        this.detectedPlatform = '';
      }

      // ユーザー名抽出
      try {
        const urlObj = new URL(this.snsUrl);
        const segments = urlObj.pathname.split('/').filter(Boolean);
        if (segments.length > 0) {
          this.detectedUsername = segments[0].replace(/^@/, '');
        } else {
          this.detectedUsername = '';
        }
      } catch {
        this.detectedUsername = '';
      }
      this.schedulePreviewUpdate();
    },
    schedulePreviewUpdate() {
      if (this.previewTimer) {
        clearTimeout(this.previewTimer);
      }
      this.previewTimer = setTimeout(() => {
        this.updateOgpPreview();
      }, 500);
    },
    async updateOgpPreview() {
      try {
        const dataUrl = await generateOgpImage(this.detectedUsername, this.comment.trim());
        this.ogpPreviewUrl = dataUrl;
      } catch (e) {
        console.warn('[PublishSettingsModal] OGPプレビュー生成失敗:', e);
        this.ogpPreviewUrl = '';
      }
    },
    publish() {
      if (this.isPublishing) return;
      this.isPublishing = true;
      const trimmedUrl = this.snsUrl.trim();
      const trimmedComment = this.comment.trim();
      if (trimmedUrl) {
        setCookie('bkms_publish_sns_url', trimmedUrl);
      }
      if (trimmedComment) {
        setCookie('bkms_publish_comment', trimmedComment);
      }
      this.$emit('publish', {
        snsUrl: trimmedUrl,
        comment: trimmedComment,
        ogpImage: this.ogpPreviewUrl || ''
      });
    },
    close() {
      this.$emit('close');
    },
    handleOverlayMouseDown(e) {
      this.mouseDownOnOverlay = e.target.classList.contains('publish-settings-overlay');
    },
    handleOverlayMouseUp(e) {
      if (this.mouseDownOnOverlay && e.target.classList.contains('publish-settings-overlay')) {
        this.close();
      }
      this.mouseDownOnOverlay = false;
    },
    handleContentMouseDown() {
      this.mouseDownOnOverlay = false;
    }
  }
};
</script>

<style scoped>
.publish-settings-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1200;
}
.publish-settings-content {
  background-color: white;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  overflow-y: auto;
}
.publish-settings-title {
  color: #333;
  margin-top: 0;
  border-bottom: 1px solid #eee;
  padding-bottom: 12px;
  font-size: 1.2rem;
}
.publish-description {
  font-size: 0.93rem;
  line-height: 1.6;
  color: #555;
  margin-bottom: 20px;
}
.sns-input-section {
  margin-bottom: 16px;
}
.sns-label {
  font-weight: 600;
  color: #333;
  display: block;
  margin-bottom: 4px;
}
.sns-hint {
  font-size: 0.82rem;
  color: #888;
  margin-bottom: 8px;
}
.sns-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.95rem;
  box-sizing: border-box;
  outline: none;
  transition: border-color 0.2s;
}
.sns-input:focus {
  border-color: #e9d0ca;
}
.platform-preview {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 8px 12px;
  background: #f8f8f8;
  border-radius: 6px;
  font-size: 0.9rem;
}
.platform-icon {
  color: #555;
}
.platform-name {
  color: #555;
  font-weight: 500;
}
.platform-username {
  color: #e9937d;
}
.publish-settings-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}
.publish-cancel-button {
  background: none;
  border: 1px solid #ddd;
  border-radius: 50px;
  padding: 10px 24px;
  font-size: 0.95rem;
  cursor: pointer;
  color: #555;
  transition: background-color 0.2s;
}
.publish-cancel-button:hover {
  background-color: #f5f5f5;
}
.publish-submit-button {
  background-color: #e9d0ca;
  color: #333;
  border: none;
  border-radius: 50px;
  padding: 10px 24px;
  font-size: 0.95rem;
  cursor: pointer;
  font-weight: 600;
  transition: background-color 0.2s;
}
.publish-submit-button:hover {
  background-color: #d9b6ab;
}
.publish-submit-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.comment-input-section {
  margin-bottom: 16px;
}
.comment-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.95rem;
  box-sizing: border-box;
  outline: none;
  transition: border-color 0.2s;
  resize: vertical;
  font-family: inherit;
  line-height: 1.5;
}
.comment-input:focus {
  border-color: #e9d0ca;
}
.comment-counter {
  text-align: right;
  font-size: 0.8rem;
  color: #aaa;
  margin-top: 4px;
}
.ogp-preview-section {
  margin-bottom: 8px;
}
.ogp-preview-container {
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
  background: #f0f0f0;
}
.ogp-preview-image {
  width: 100%;
  display: block;
}
.ogp-preview-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 40px 0;
  color: #aaa;
  font-size: 0.85rem;
}

@media (max-width: 600px) {
  .publish-settings-content {
    padding: 20px;
  }
}
</style>
